import React from 'react'
import OverlayRail from './OverlayRail'
import ParticipantTile from './ParticipantTile'

function mapParticipantsById(participants = []) {
  return participants.reduce((participantMap, participant) => {
    if (participant?.id) {
      participantMap[participant.id] = participant
    }

    return participantMap
  }, {})
}

function resolveParticipants(participantIds = [], participantMap = {}) {
  return participantIds
    .map((participantId) => participantMap[participantId] || null)
    .filter(Boolean)
}

export default function VideoLayoutRenderer({
  participants = [],
  layoutModel,
  videoBinder,
  onSelectParticipant,
  onExpandRail,
  debugContext = null,
}) {
  const participantMap = React.useMemo(() => mapParticipantsById(participants), [participants])
  const primaryParticipant = layoutModel?.primaryParticipantId
    ? participantMap[layoutModel.primaryParticipantId] || null
    : null
  const secondaryParticipants = resolveParticipants(
    layoutModel?.secondaryParticipantIds,
    participantMap,
  )
  const gridParticipants = resolveParticipants(layoutModel?.gridParticipantIds, participantMap)
  const railParticipants = resolveParticipants(layoutModel?.railParticipantIds, participantMap)
  const selfPreviewParticipant = layoutModel?.selfPreviewParticipantId
    ? participantMap[layoutModel.selfPreviewParticipantId] || null
    : null
  const rootClassName = `shared-video-layout shared-video-layout--${layoutModel?.layoutId || 'stage-overlay'}${
    layoutModel?.screenShareHeroMode ? ' shared-video-layout--screen-share' : ''
  }`

  if (layoutModel?.layoutId === 'grid') {
    return (
      <div className={rootClassName}>
        {gridParticipants.map((participant) => (
          <ParticipantTile
            key={participant.id}
            participant={participant}
            videoBinder={videoBinder}
            debugContext={debugContext}
          />
        ))}
      </div>
    )
  }

  if (layoutModel?.layoutId === 'split') {
    return (
      <div className={rootClassName}>
        {primaryParticipant ? (
          <div className="shared-video-layout__split-top">
            <div className="shared-video-layout__split-primary">
              <ParticipantTile
                participant={primaryParticipant}
                videoBinder={videoBinder}
                isPrimary
                debugContext={debugContext}
              />
            </div>
            {secondaryParticipants.length ? (
              <div className="shared-video-layout__split-secondary">
                {secondaryParticipants.map((participant) => (
                  <ParticipantTile
                    key={participant.id}
                    participant={participant}
                    videoBinder={videoBinder}
                    onClick={onSelectParticipant}
                    debugContext={debugContext}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {gridParticipants.length ? (
          <div className="shared-video-layout__split-grid">
            {gridParticipants.map((participant) => (
              <ParticipantTile
                key={participant.id}
                participant={participant}
                videoBinder={videoBinder}
                onClick={onSelectParticipant}
                debugContext={debugContext}
              />
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className={rootClassName}>
      {primaryParticipant ? (
        <div className="shared-video-layout__stage">
          <ParticipantTile
            participant={primaryParticipant}
            videoBinder={videoBinder}
            isPrimary
            debugContext={debugContext}
          />
        </div>
      ) : null}

      {layoutModel?.hasRail ? (
        <OverlayRail
          participants={railParticipants}
          railStateId={layoutModel?.railStateId}
          videoBinder={videoBinder}
          onSelectParticipant={onSelectParticipant}
          onExpandRail={onExpandRail}
          compact={Boolean(layoutModel?.screenShareHeroMode)}
          carousel={layoutModel?.layoutId === 'stage-overlay'}
          debugContext={debugContext}
        />
      ) : null}

      {selfPreviewParticipant ? (
        <div className="shared-video-layout__self-preview">
          <ParticipantTile
            participant={selfPreviewParticipant}
            videoBinder={videoBinder}
            debugContext={debugContext}
          />
        </div>
      ) : null}
    </div>
  )
}

VideoLayoutRenderer.displayName = 'VideoLayoutRenderer'
